import { FC, useEffect, useRef, useState } from 'react';
import { Stack, Button, Box, Text, Loader, Switch, Group } from '@mantine/core';
import { IconCapture, IconScan, IconCamera } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './DocumentScanner.module.css';

interface CameraViewProps {
  stream: MediaStream | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onCapture: () => void;
  onHighlight?: () => HTMLCanvasElement | null;
  documentMode?: boolean;
  onDocumentModeChange?: (mode: boolean) => void;
}

export const CameraView: FC<CameraViewProps> = ({
  stream,
  videoRef,
  canvasRef,
  onCapture,
  onHighlight,
  documentMode = true,
  onDocumentModeChange,
}) => {
  const { t } = useTranslation(['common']);
  const [videoReady, setVideoReady] = useState(false);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const intervalRef = useRef<NodeJS.Timeout>();

  // Initialize video stream
  useEffect(() => {
    const video = videoRef.current;
    if (stream && video) {
      video.srcObject = stream;
      video.onloadedmetadata = () => {
        video.play();

        // Set canvas sizes ONCE when video is ready (matching jscanify example)
        if (canvasRef.current && overlayCanvasRef.current) {
          canvasRef.current.width = video.videoWidth;
          canvasRef.current.height = video.videoHeight;
          overlayCanvasRef.current.width = video.videoWidth;
          overlayCanvasRef.current.height = video.videoHeight;
        }

        setVideoReady(true);
      };
    }

    return () => {
      if (video) {
        video.srcObject = null;
      }
    };
  }, [stream, videoRef, canvasRef]);

  // Real-time document highlighting (only when document mode is ON)
  // Following jscanify documentation: use setInterval as recommended
  useEffect(() => {
    // Clear overlay if document mode is OFF
    if (!documentMode && overlayCanvasRef.current) {
      const ctx = overlayCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          overlayCanvasRef.current.width,
          overlayCanvasRef.current.height
        );
      }
    }

    if (!videoReady || !onHighlight || !overlayCanvasRef.current || !documentMode) {
      return;
    }

    const updateHighlight = () => {
      try {
        const highlightedCanvas = onHighlight();

        if (highlightedCanvas && overlayCanvasRef.current) {
          const ctx = overlayCanvasRef.current.getContext('2d', {
            willReadFrequently: true,
          });
          if (ctx) {
            // Follow jscanify example pattern exactly:
            // Clear canvas, then draw result
            ctx.clearRect(
              0,
              0,
              overlayCanvasRef.current.width,
              overlayCanvasRef.current.height
            );
            ctx.drawImage(highlightedCanvas, 0, 0);
          }
        }
      } catch (error) {
        console.error('Error updating highlight:', error);
      }
    };

    // Use same interval as jscanify example (200ms = 5fps for stable detection)
    intervalRef.current = setInterval(updateHighlight, 200);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [videoReady, onHighlight, videoRef, documentMode]);

  return (
    <Stack gap="md" align="center">
      {/* Document Mode Toggle */}
      {onDocumentModeChange && (
        <Group gap="sm" className={classes.modeToggle}>
          <IconCamera size={18} opacity={documentMode ? 0.5 : 1} />
          <Switch
            checked={documentMode}
            onChange={(event) => onDocumentModeChange(event.currentTarget.checked)}
            size="md"
            aria-label={t('documentScanner.documentMode', {
              defaultValue: 'Document Mode',
            })}
          />
          <IconScan size={18} opacity={documentMode ? 1 : 0.5} />
          <Text size="sm" fw={500}>
            {documentMode
              ? t('documentScanner.documentMode', { defaultValue: 'Document Mode' })
              : t('documentScanner.photoMode', { defaultValue: 'Photo Mode' })}
          </Text>
        </Group>
      )}

      <Box className={classes.videoContainer}>
        {/* Video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={videoReady ? classes.video : classes.videoHidden}
        />

        {/* Overlay canvas for document highlighting */}
        <canvas
          ref={overlayCanvasRef}
          className={
            videoReady ? classes.overlayCanvas : classes.overlayCanvasHidden
          }
        />

        {/* Hidden canvas for processing */}
        <canvas ref={canvasRef} className={classes.hiddenCanvas} />

        {/* Loading indicator */}
        {!videoReady && (
          <Box className={classes.loadingContainer}>
            <Stack align="center" justify="center" h="100%">
              <Loader size="lg" c="white" />
              <Text c="white" size="sm">
                {t('documentScanner.initializingCamera', {
                  defaultValue: 'Initializing camera...',
                })}
              </Text>
            </Stack>
          </Box>
        )}

        {/* Instructions overlay */}
        {videoReady && (
          <Box className={classes.instructionsOverlay}>
            <Text size="sm" fw={500}>
              {documentMode
                ? t('documentScanner.positionDocument', {
                    defaultValue: 'Position document within the frame',
                  })
                : t('documentScanner.takePhoto', {
                    defaultValue: 'Take a photo',
                  })}
            </Text>
          </Box>
        )}
      </Box>

      {/* Capture button */}
      <Button
        size="lg"
        leftSection={documentMode ? <IconScan size={20} /> : <IconCapture size={20} />}
        onClick={onCapture}
        disabled={!videoReady}
        className={classes.captureButton}
        fullWidth
      >
        {documentMode
          ? t('documentScanner.capture', {
              defaultValue: 'Capture Document',
            })
          : t('documentScanner.takePhotoButton', {
              defaultValue: 'Take Photo',
            })}
      </Button>
    </Stack>
  );
};
